import { Menu, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { campaignThemes } from "../data/campaignThemes";

const links = [
  ["/", "Início"],
  ["/traiano", "Sobre"],
  ["/#investimentos", "Projetos"],
  ["/avatar", "Avatar"],
  ["/#redes-sociais", "Redes sociais"],
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [themeIndex, setThemeIndex] = useState(0);
  const reduced = useReducedMotion();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const theme = campaignThemes[themeIndex];
  const ThemeIcon = theme.icon;

  useEffect(() => {
    const query = matchMedia('(max-width: 800px)');
    const update = () => setMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!mobile || reduced) return;
    const interval = window.setInterval(
      () => setThemeIndex((current) => (current + 1) % campaignThemes.length),
      5600,
    );
    return () => window.clearInterval(interval);
  }, [mobile, reduced]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const regions = [...document.querySelectorAll<HTMLElement>('main, .footer')];
    const previousInert = regions.map((element) => element.inert);
    regions.forEach((element) => { element.inert = true; });
    const frame = requestAnimationFrame(() => document.querySelector<HTMLAnchorElement>('#mobile-navigation a')?.focus());
    const closeOnDesktop = () => { if (innerWidth >= 1280) setOpen(false); };
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = [...document.querySelectorAll<HTMLElement>('.menu-button, #mobile-navigation a')];
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    addEventListener('resize', closeOnDesktop);
    addEventListener('keydown', trapFocus);
    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      regions.forEach((element, index) => { element.inert = previousInert[index]; });
      removeEventListener('resize', closeOnDesktop);
      removeEventListener('keydown', trapFocus);
      document.querySelector<HTMLButtonElement>('.menu-button')?.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) =>
      event.key === "Escape" && setOpen(false);
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const update = () => {
      if (!isHome) {
        setScrolled(scrollY > 24);
        return;
      }
      const hero = document.querySelector<HTMLElement>('.home-hero');
      setScrolled(Boolean(hero && hero.getBoundingClientRect().bottom <= 0));
    };
    const frame = requestAnimationFrame(update);
    addEventListener("scroll", update, { passive: true });
    addEventListener("resize", update, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("scroll", update);
      removeEventListener("resize", update);
    };
  }, [isHome]);

  return (
    <>
      <header className={`site-header ${isHome ? "site-header--home" : ""} ${scrolled ? "is-scrolled" : ""}`.trim()}>
        <div className="shell flex h-[76px] items-center justify-between gap-8">
          <NavLink to="/" className="brand">
            <img src={`${import.meta.env.BASE_URL}assets/images/traianologo.png`} alt="Traiano 55789 — Deputado Estadual" />
          </NavLink>
          {mobile && (
            <div className="mobile-theme" aria-label="Mais Educação, Saúde, Infraestrutura e Oportunidades">
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme.label}
                  initial={reduced ? false : { opacity: 0, y: 6, filter: 'blur(3px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={reduced ? undefined : { opacity: 0, y: -6, filter: 'blur(3px)' }}
                  transition={{ duration: 0.72, ease: [0.16, 1, 0.3, 1] }}
                  aria-hidden="true"
                >
                  <ThemeIcon />
                  {theme.label}
                </motion.span>
              </AnimatePresence>
            </div>
          )}
          <nav
            className="hidden items-center gap-5 xl:flex"
            aria-label="Navegação principal"
          >
            {links.map(([to, label]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  isActive && !to.includes("#") ? "active" : undefined
                }
              >
                {label}
              </NavLink>
            ))}
            <a className="nav-cta" href={`${import.meta.env.BASE_URL}#redes-sociais`}>
              Acompanhe <span aria-hidden="true">→</span>
            </a>
          </nav>
          <button
            className="menu-button xl:hidden"
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
          >
            {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-navigation"
            className="mobile-nav"
            aria-label="Navegação móvel"
            initial={reduced ? false : { clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={reduced ? undefined : { clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="shell flex flex-col py-8">
              {links.map(([to, label], index) => (
                <motion.div
                  key={to}
                  initial={reduced ? false : { opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.035 }}
                >
                  <NavLink
                    to={to}
                    end={to === "/"}
                    className={({ isActive }) =>
                      isActive && !to.includes("#") ? "active" : undefined
                    }
                    onClick={() => setOpen(false)}
                  >
                    {label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={reduced ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: links.length * 0.035 }}
              >
                <a
                  href={`${import.meta.env.BASE_URL}#redes-sociais`}
                  onClick={() => setOpen(false)}
                >
                  Acompanhe
                </a>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
